import {
    BuildingStorefrontIcon,
    FireIcon,
    TruckIcon,
    UserGroupIcon,
    UsersIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AdminApiService } from '../../utils/api';

const StatCard = ({ title, value, change, icon: Icon, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-lg transition-all duration-300"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        {change && (
          <div className={`flex items-center mt-2 text-sm ${
            trend === 'up' ? 'text-success-600' : 'text-danger-600'
          }`}>
            <FireIcon className={`w-4 h-4 mr-1 ${trend === 'down' ? 'rotate-180' : ''}`} />
            <span>{change} from last month</span>
          </div>
        )}
      </div>
      <div className={`p-4 rounded-xl ${color}`}>
        <Icon className="w-8 h-8 text-white" />
      </div>
    </div>
  </motion.div>
);

const Dashboard = () => {
  const [analytics, setAnalytics] = useState({
    overview: {
      totalAgents: 0,
      totalCars: 0,
      activeSellers: 0,
      totalBuyers: 0,
      monthlyRevenue: 0
    },
    trends: {
      revenue: [],
      carListings: []
    }
  });

  useEffect(() => {
    const fetchDashboardAnalytics = async () => {
      try {
        const response = await AdminApiService.getAnalytics();
        const payload = response?.data || {};

        setAnalytics({
          overview: payload.overview || {
            totalAgents: 0,
            totalCars: 0,
            activeSellers: 0,
            totalBuyers: 0,
            monthlyRevenue: 0
          },
          trends: {
            revenue: payload.trends?.revenue || [],
            carListings: payload.trends?.carListings || []
          }
        });
      } catch (error) {
        console.error('Failed to fetch dashboard analytics:', error);
        toast.error('Failed to load dashboard analytics');
      }
    };

    fetchDashboardAnalytics();
  }, []);

  const { overview, trends } = analytics;

  const pieData = useMemo(() => {
    const activeSellers = Number(overview.activeSellers) || 0;
    const totalBuyers = Number(overview.totalBuyers) || 0;
    const totalUsers = activeSellers + totalBuyers;

    if (!totalUsers) {
      return [
        { name: 'Active Sellers', value: 0, color: '#f97316' },
        { name: 'Registered Buyers', value: 0, color: '#0ea5e9' }
      ];
    }

    return [
      { name: 'Active Sellers', value: Math.round((activeSellers / totalUsers) * 100), color: '#f97316' },
      { name: 'Registered Buyers', value: Math.round((totalBuyers / totalUsers) * 100), color: '#0ea5e9' }
    ];
  }, [overview.activeSellers, overview.totalBuyers]);

  const recentActivity = [
    { type: 'agent', message: 'New premium agent registered', time: '2 mins ago', color: 'bg-success-100 text-success-600' },
    { type: 'car', message: 'High-value car listed (₹45L)', time: '5 mins ago', color: 'bg-primary-100 text-primary-600' },
    { type: 'sale', message: 'Car sold by Rajesh Kumar', time: '12 mins ago', color: 'bg-secondary-100 text-secondary-600' },
    { type: 'verification', message: 'Seller documents verified', time: '18 mins ago', color: 'bg-warning-100 text-warning-600' }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Dashboard Overview
          </h1>
          <p className="text-gray-600 mt-2">
            Welcome back! Here's what's happening with your marketplace.
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center space-x-4 mt-4 lg:mt-0"
        >
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-600">₹{(overview.monthlyRevenue / 10000000).toFixed(1)}Cr</div>
            <div className="text-sm text-gray-500">This Month Revenue</div>
          </div>
        </motion.div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Agents"
          value={overview.totalAgents.toLocaleString()}
          icon={UsersIcon}
          color="bg-gradient-to-br from-primary-500 to-primary-600"
        />
        <StatCard
          title="Listed Cars"
          value={overview.totalCars.toLocaleString()}
          icon={TruckIcon}
          color="bg-gradient-to-br from-secondary-500 to-secondary-600"
        />
        <StatCard
          title="Active Sellers"
          value={overview.activeSellers.toLocaleString()}
          icon={BuildingStorefrontIcon}
          color="bg-gradient-to-br from-success-500 to-success-600"
        />
        <StatCard
          title="Registered Buyers"
          value={overview.totalBuyers.toLocaleString()}
          icon={UserGroupIcon}
          color="bg-gradient-to-br from-warning-500 to-warning-600"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
              <span>Monthly Revenue (₹)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trends.revenue}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `₹${(value/10000000).toFixed(1)}Cr`} />
              <Tooltip formatter={(value) => [`₹${(value/10000000).toFixed(1)}Cr`, 'Revenue']} />
              <Area type="monotone" dataKey="value" stroke="#f97316" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={3} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Agent Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">User Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-4">
            {pieData.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-gray-600">{item.name}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{item.value}%</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Car Listings Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Car Listings Growth</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={trends.carListings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `${(value/1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => [value.toLocaleString(), 'Cars Listed']} />
              <Bar dataKey="value" fill="url(#colorBar)" radius={[4, 4, 0, 0]} />
              <defs>
                <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.4}/>
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className={`w-2 h-2 rounded-full ${activity.color.split(' ')[0]}`}></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-orange rounded-xl p-6 text-white"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-xl font-semibold mb-2">Quick Actions</h3>
            <p className="text-orange-100">Manage your marketplace efficiently</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-4 lg:mt-0">
            <button className="btn-secondary text-primary-600 hover:text-primary-700">
              Create Agent
            </button>
            <button className="btn-secondary text-primary-600 hover:text-primary-700">
              Verify Documents
            </button>
            <button className="btn-secondary text-primary-600 hover:text-primary-700">
              View Reports
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;